import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { IControlTooltip, ControlTooltipODataService } from '@exchange/domain/.'
import { PagedResult } from 'common-services'

export interface ITooltipComponent {
  form: string
  uid: string
  value: IControlTooltip
  hide: () => void
}

class FormTooltips {
  private tooltips: Map<string, [ITooltipComponent, IControlTooltip]> = new Map<
    string,
    [ITooltipComponent, IControlTooltip]
  >()
  private timeout: any = null

  constructor(public name: string) {}

  add(tooltip: ITooltipComponent): IControlTooltip {
    let stored: [ITooltipComponent, IControlTooltip] = this.tooltips.get(tooltip.uid)
    if (!stored) {
      stored = [
        tooltip,
        {
          Id: 0,
          Title: '',
          Text: '',
          Form: tooltip.form,
          Uid: tooltip.uid
        } as IControlTooltip
      ]
      this.tooltips.set(tooltip.uid, stored)
    } else {
      stored[0] = tooltip
    }
    tooltip.value = stored[1]

    return tooltip.value
  }

  setValues(values: IControlTooltip[]): void {
    values
      .filter(x => x.Form === this.name)
      .forEach(x => {
        let stored: [ITooltipComponent, IControlTooltip] = this.tooltips.get(x.Uid)
        if (!stored) {
          stored = [null, x]
          this.tooltips.set(x.Uid, stored)
        } else {
          const tooltip: ITooltipComponent = stored[0]
          tooltip.value = stored[1] = x
        }
      })
  }

  fetch(observable: Observable<PagedResult<IControlTooltip>>): void {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }

    this.timeout = setTimeout(() => {
      observable.subscribe(results => this.setValues(results.data))
      this.timeout = null
    }, 100)
  }

  invalidate(): void {
    this.timeout = null
    for (const tooltip of Array.from(this.tooltips.values())) {
      tooltip[1] = null
    }
  }
}

@Injectable()
export class TooltipService {
  private forms: Map<string, FormTooltips> = new Map<string, FormTooltips>()
  private _current: ITooltipComponent = null
  public get current(): ITooltipComponent {
    return this._current
  }
  public set current(value: ITooltipComponent) {
    if (this._current && this._current !== value) {
      this._current.hide()
    }
    this._current = value
  }

  constructor(private odata: ControlTooltipODataService) {}

  public register(tooltip: ITooltipComponent): void {
    const form: FormTooltips = this.getFormTooltips(tooltip)
    form.add(tooltip)
    this.fetchForm(form)
  }

  public update(
    tooltip: ITooltipComponent,
    success: (response: any) => void,
    error: (error: any) => void
  ): void {
    if (tooltip.value.Id) {
      this.odata
        .put(tooltip.value, tooltip.value.Id)
        .exec()
        .subscribe(response => {
          this.updateSuccess(tooltip, response, success)
        }, error.bind(tooltip))
    } else {
      this.odata
        .post(tooltip.value)
        .exec()
        .subscribe(response => {
          this.updateSuccess(tooltip, response, success)
        }, error.bind(tooltip))
    }
  }

  private getFormTooltips(tooltip: ITooltipComponent): FormTooltips {
    let stored: FormTooltips = this.forms.get(tooltip.form)
    if (!stored) {
      stored = new FormTooltips(tooltip.form)
      this.forms.set(tooltip.form, stored)
    }

    return stored
  }

  private fetchForm(form: FormTooltips): void {
    form.fetch(this.odata.query().filter(`Form eq '${form.name}'`).exec())
  }

  private updateSuccess(tooltip, response, success: (response: any) => void): void {
    const form: FormTooltips = this.getFormTooltips(tooltip)
    form.invalidate()
    this.fetchForm(form)

    success.call(tooltip, response)
  }

  hideCurrent(e: any): void {
    for (let p: any = e.target; !!p; p = p.parentNode) {
      if (p.classList && p.classList.contains('tooltip-panel')) {
        return
      }
    }
    this.current = null
  }
}
